// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { store } from "../../store";
import { setModalOpen } from "../../store/modalSlice";
import { createDatasetAction, fetchDatasetById } from "../../store/datasetSlice";
import {
  datasetUploadEventProperties,
  datasetUploadFileError,
  describeDatasetUpload,
  formatBytes,
  runDatasetUpload,
  sentUploadPlaceholder,
  uploadTimezoneName,
  type DatasetUploadLink,
} from "../../utils/datasetUploadApi";
import { getIngestTimezone } from "../../utils/datasetIngestSettings";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import DatasetUploadsList from "../Datasets/DatasetUploadsList";
import {
  generateSlug,
  generateUniqueSlug,
  generateDatasetId
} from "../../utils/datasetUtils";
import { ReactComponent as LayersIcon } from "../../assets/icons/layers-icon.svg";
import { ReactComponent as ChevronRightIcon } from "../../assets/icons/chevron-right.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-02.svg";
import ModalBase from "./ModalBase";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { TIME_ZONE_OPTIONS } from "../../utils/timeZoneOptions";

import Select from "../ReusableComponents/Select";
const STAGE_LABELS = {
  requesting: "Requesting upload link",
  uploading: "Uploading file",
  processing: "Processing",
};
const STAGE_DETAILS = {
  requesting: "Checking access and preparing a secure upload link…",
  uploading: "Sending the file to storage…",
  processing: "The file arrived; it is being checked and loaded. Large backfills can take a minute or two.",
};

// The form's seed, at module scope so a view switch can re-run it — ModalGlobal maps both newHistoricalDataset and uploadDatasetData to this component, so React reuses the instance and a useState initializer alone would leave the create form's blank state in upload mode.
const buildFormState = (uploadMode, existingDataset) => {
    if (uploadMode && existingDataset) {
      // Initialize with existing dataset data for upload mode
      return {
        name: existingDataset.name || "",
        description: existingDataset.description || "",
        datasetType: existingDataset.datasetType || "HISTORICAL",
        location: existingDataset.location || "",
        locationLat: existingDataset.locationLat || "",
        locationLon: existingDataset.locationLon || "",
        ownerType: existingDataset.organizationId ? "organization" : "personal",
        selectedOrgKey: "",
        slug: existingDataset.slug || "",
        generatedSlug: existingDataset.slug || "",
      };
    }
    // Default state for new dataset creation. No column list: `Dataset.columns` is what uploads have carried,
    // written by ingest, so a dataset that has not held data lists none (#871)
    return {
      name: "",
      description: "",
      datasetType: "HISTORICAL",
      location: "",
      locationLat: "",
      locationLon: "",
      ownerType: "personal",
      selectedOrgKey: "",
      slug: "",
      generatedSlug: "",
    };
};

const HistoricalDatasetModal = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const modalState = useSelector((state) => state.modalState);
  const { capture } = usePostHogCapture();

  // Determine mode from modal state
  const uploadMode = modalState.modalView === 'uploadDatasetData';
  const existingDataset = uploadMode ? modalState.properties?.dataset : null;

  // Form state
  const [currentStep, setCurrentStep] = useState(uploadMode ? 2 : 1);
  const [createdDatasetId, setCreatedDatasetId] = useState(existingDataset?.id || null);
  const [formData, setFormData] = useState(() => buildFormState(uploadMode, existingDataset));

  // Upload step. The file goes through the ingest pipeline like an API push (src/utils/datasetUploadApi.ts):
  // stage is null (idle), "requesting" (asking for a link), "uploading" (file → S3, percent known) or
  // "processing" (ingest at work, polled); uploadResult holds the final upload row once there is one.
  const [csvFile, setCsvFile] = useState(null);
  const [uploadStage, setUploadStage] = useState(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadsRefreshKey, setUploadsRefreshKey] = useState(0);
  const uploadAbort = useRef(null);
  const uploadLink = useRef<DatasetUploadLink | null>(null); // the current upload's issued link, for the stop-waiting placeholder
  const [uploadStatus, setUploadStatus] = useState("");
  const [errors, setErrors] = useState({});
  const isUploading = uploadStage !== null;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ModalGlobal maps both dataset views to this one component, so React reuses the instance and the initializers above never re-run — opening Upload Data after Create left the blank create form behind a title naming the dataset. Re-seed whenever the view or its dataset changes.
  const seedKey = `${modalState.modalView}:${existingDataset?.id || ""}`;
  const seededFor = useRef(seedKey);
  useEffect(() => {
    if (seededFor.current === seedKey) return;
    seededFor.current = seedKey;
    setCurrentStep(uploadMode ? 2 : 1);
    setCreatedDatasetId(existingDataset?.id || null);
    setFormData(buildFormState(uploadMode, existingDataset));
    setCsvFile(null);
    setUploadResult(null);
    setUploadStage(null);
    setUploadPercent(0);
    setForecastIssueTime("");
    autoIssueTime.current = "";
    setOverrideTimezone(false);
    setErrors({});
    setUploadStatus("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey]);

  // A poll left running after the modal unmounts would set state on a dead component
  useEffect(() => () => uploadAbort.current?.abort(), []);

  // Timezone for timestamps that carry no offset. By default none is sent, so ingest applies the dataset's
  // ingest settings (or UTC with a warning); the override below sends sourceTimezone with the upload.
  const [overrideTimezone, setOverrideTimezone] = useState(false);
  const [timezoneType, setTimezoneType] = useState(() => {
    return localStorage.getItem('preferredTimezoneType') || 'named';
  });
  const [sourceTimeZone, setSourceTimeZone] = useState(() => {
    return localStorage.getItem('preferredTimeZone') || 'UTC';
  });
  const [utcOffset, setUtcOffset] = useState(() => {
    return parseFloat(localStorage.getItem('preferredUtcOffset')) || 0;
  });
  
  // Save preferences when they change
  useEffect(() => {
    localStorage.setItem('preferredTimezoneType', timezoneType);
    localStorage.setItem('preferredTimeZone', sourceTimeZone);
    localStorage.setItem('preferredUtcOffset', utcOffset.toString());
  }, [timezoneType, sourceTimeZone, utcOffset]);

  // Get organizations for dropdown
  const organizations = Object.entries(user?.organizations?.data || {}).map(([key, org]) => ({
    id: key,
    name: org.name
  }));

  // Auto-generate slug when name changes
  useEffect(() => {
    if (formData.name) {
      const slug = generateSlug(formData.name);
      setFormData(prev => ({ ...prev, generatedSlug: slug }));
    }
  }, [formData.name]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  // FORECAST / ENSEMBLE_FORECAST datasets are retired (phase 3 of docs/plans/DATA_PIPELINES_CONVERGENCE_PLAN.md):
  // uploads into one are refused by ingest; forecasts made elsewhere go into an external forecast model's output
  const isRetiredForecastDataset =
    existingDataset?.datasetType === 'FORECAST' || existingDataset?.datasetType === 'ENSEMBLE_FORECAST';

  const selectFile = (file) => {
    if (!file) return;
    const problem = datasetUploadFileError(file);
    if (problem) {
      setCsvFile(null);
      setErrors({ csvFile: problem });
      return;
    }
    setCsvFile(file);
    setUploadResult(null);
    setErrors({});
  };

  const handleFileChange = (e) => {
    selectFile(e.target.files?.[0]);
    e.target.value = ""; // so picking the same file again after a result still fires onChange
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (isUploading) return;
    selectFile(e.dataTransfer?.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Dataset name is required";
    }
    
    if (!formData.datasetType) {
      newErrors.datasetType = "Dataset type is required";
    }

    if (formData.ownerType === "organization" && !formData.selectedOrgKey) {
      newErrors.selectedOrgKey = "Please select an organization";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep1()) return;

    setIsSubmitting(true);
    setUploadStatus("Creating dataset...");

    try {
      const authUser = await getCurrentUser();
      const cognitoUserId = authUser?.userId;

      // Generate unique slug
      const organizationId = formData.ownerType === "organization" ? formData.selectedOrgKey : null;
      const uniqueSlug = await generateUniqueSlug(
        organizationId || "",  // Pass empty string for personal datasets
        formData.name
      );

      // Generate dataset ID
      const datasetId = generateDatasetId(formData.datasetType, formData.name);

      // Build location object
      let location = null;
      if (formData.location) {
        location = { name: formData.location };
        if (formData.locationLat && formData.locationLon) {
          location.lat = parseFloat(formData.locationLat);
          location.lon = parseFloat(formData.locationLon);
        }
      }

      const ownerId = cognitoUserId || user?.permissionDetails?.id;
      if (!ownerId && !organizationId) {
        throw new Error("Unable to determine user identity for dataset ownership.");
      }

      // Get cognito identity ID from AWS Amplify Auth session (for S3 private paths)
      const session = await fetchAuthSession();
      const cognitoIdentityId = session.identityId;
      if (!cognitoIdentityId && !organizationId) {
        throw new Error("Unable to get Cognito Identity ID for personal dataset storage.");
      }

      // Create dataset input
      const datasetInput = {
        id: datasetId,
        slug: uniqueSlug,
        name: formData.name,
        description: formData.description || null,
        datasetType: formData.datasetType,
        location: location ? JSON.stringify(location) : null,
        fileFormat: "CSV",
        owner: ownerId,
        userId: organizationId ? null : cognitoUserId,
        organizationId: organizationId,  // Set organizationId for org datasets
        // Pass cognitoIdentityId separately for S3 path generation
        cognitoIdentityId: cognitoIdentityId,
      };

      console.log("Creating dataset with input:", datasetInput);
      console.log("User data:", user);
      console.log("Cognito Identity ID from fetchAuthSession:", cognitoIdentityId);
      console.log("Organization ID:", organizationId);
      
      // Create dataset
      const result = await dispatch(createDatasetAction(datasetInput));
      
      if (createDatasetAction.fulfilled.match(result)) {
        setCreatedDatasetId(result.payload.id);
        setFormData(prev => ({ ...prev, slug: uniqueSlug }));
        setCurrentStep(2);
        setUploadStatus("");
      } else {
        console.error("Dataset creation failed:", result);
        console.error("Error details:", result.error);
        console.error("Payload:", result.payload);
        throw new Error(result.error?.message || result.payload || "Failed to create dataset");
      }
    } catch (error) {
      console.error("Error creating dataset:", error);
      setErrors({ submit: error.message });
      setUploadStatus("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadData = async () => {
    const problem = datasetUploadFileError(csvFile);
    if (problem) {
      setErrors({ csvFile: problem });
      return;
    }

    // Get the created dataset from the store
    const state = store.getState();
    const dataset =
      state.datasets.personalDatasets[createdDatasetId] ||
      state.datasets.organizationDatasets[createdDatasetId];
    if (!dataset) {
      setErrors({ upload: "Dataset not found" });
      return;
    }

    const sourceTimezone = overrideTimezone ? uploadTimezoneName(timezoneType, sourceTimeZone, utcOffset) : null;

    const controller = new AbortController();
    uploadAbort.current = controller;
    setErrors({});
    setUploadResult(null);
    setUploadPercent(0);
    setUploadStage("requesting");
    uploadLink.current = null;

    try {
      const sentFile = csvFile;
      const result = await runDatasetUpload(dataset.id, sentFile, {
        fileName: csvFile.name,
        sourceTimezone,
        signal: controller.signal,
        onStage: (stage) => setUploadStage(stage),
        onLinkIssued: (link) => { uploadLink.current = link; },
        onUploadProgress: (sent, total) => setUploadPercent(total ? Math.round((sent / total) * 100) : 0),
      });
      if (controller.signal.aborted) return;
      setUploadResult(result);
      setCsvFile(null); // the file is in S3 whatever the outcome; the next upload starts from a fresh pick
      setUploadsRefreshKey((k) => k + 1);
      capture(POSTHOG_EVENTS.DATASET_UPLOAD_COMPLETED, datasetUploadEventProperties(dataset, sentFile, { result, fromCreateFlow: !uploadMode }));
      if (result.upload.status === "LOADED") {
        // Ingest updated the count, time range and columns server-side; pull them into the store
        dispatch(fetchDatasetById({ datasetId: dataset.id }));
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Error uploading data:", error);
      setErrors({ upload: error.message });
      capture(POSTHOG_EVENTS.DATASET_UPLOAD_COMPLETED, datasetUploadEventProperties(dataset, csvFile, { fromCreateFlow: !uploadMode }));
    } finally {
      if (uploadAbort.current === controller) uploadAbort.current = null;
      if (!controller.signal.aborted) setUploadStage(null);
    }
  };

  // Before the file is sent, cancel really cancels. Once S3 has it, ingest loads it regardless, so "stop
  // waiting" only stops the poll and hands the outcome to Recent uploads — never back to a re-uploadable state.
  const handleCancelUpload = () => {
    const stage = uploadStage;
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setUploadStage(null);
    setUploadPercent(0);
    if (stage === "processing" && csvFile) {
      setUploadResult({ upload: sentUploadPlaceholder(uploadLink.current ?? { uploadId: "", url: "", fields: {}, expiresAt: "", maxBytes: 0 }, csvFile), timedOut: true });
      setCsvFile(null);
      setUploadsRefreshKey((k) => k + 1);
    }
  };

  const handleUploadAnother = () => {
    setCsvFile(null);
    setUploadResult(null);
    setForecastIssueTime("");
    autoIssueTime.current = "";
    setErrors({});
  };

  const handleClose = () => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    dispatch(setModalOpen(false));
    // Reset form
    setFormData({
      name: "",
      description: "",
      datasetType: "HISTORICAL",
      location: "",
      locationLat: "",
      locationLon: "",
      ownerType: "personal",
      selectedOrgKey: "",
      slug: "",
      generatedSlug: "",
    });
    setCsvFile(null);
    setUploadResult(null);
    setUploadStage(null);
    setUploadPercent(0);
    setCurrentStep(1);
    setCreatedDatasetId(null);
    setErrors({});
  };

  const handleSkipUpload = () => {
    handleClose();
  };

  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <div className="space-y-6">
          {/* Dataset Name */}
          <div>
            <label className="eai-meta-label mb-2 block">
              Dataset Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-eai-cta-primary ${
                errors.name ? 'border-eai-danger-border-strong' : 'border-eai-border-hover'
              }`}
              placeholder="e.g., River Temperature Historical"
            />
            {formData.generatedSlug && (
              <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                Slug: {formData.generatedSlug}
              </p>
            )}
            {errors.name && (
              <p className="mt-1 text-eai-body-sm text-eai-danger-text">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="eai-meta-label mb-2 block">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              className="eai-input"
              placeholder="Describe what this dataset contains"
            />
          </div>

          {/* Dataset type: HISTORICAL is the only kind a dataset can be; forecasts go into an external forecast model */}
          <div>
            <label className="eai-meta-label mb-2 block">Dataset Type</label>
            <p className="text-eai-body-sm text-eai-ink">Historical</p>
            <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
              A dataset holds historical data. Forecasts made outside the platform are uploaded to an external forecast model instead.
            </p>
          </div>

          {/* Location (Optional) */}
          <div className="space-y-4">
            <div>
              <label className="eai-meta-label mb-2 block">
                Location Name (Optional)
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                className="eai-input"
                placeholder="e.g., Station NSR-123"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="eai-meta-label mb-2 block">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.locationLat}
                  onChange={(e) => handleInputChange("locationLat", e.target.value)}
                  className="eai-input"
                  placeholder="53.5461"
                />
              </div>
              <div>
                <label className="eai-meta-label mb-2 block">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.locationLon}
                  onChange={(e) => handleInputChange("locationLon", e.target.value)}
                  className="eai-input"
                  placeholder="-113.4938"
                />
              </div>
            </div>
          </div>

          {/* Ownership */}
          <div>
            <label className="eai-meta-label mb-2 block">
              Ownership
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="personal"
                  checked={formData.ownerType === "personal"}
                  onChange={(e) => handleInputChange("ownerType", e.target.value)}
                  className="mr-2"
                />
                Personal
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="organization"
                  checked={formData.ownerType === "organization"}
                  onChange={(e) => handleInputChange("ownerType", e.target.value)}
                  className="mr-2"
                />
                Organization
              </label>
            </div>

            {formData.ownerType === "organization" && (
              <div className="mt-3">
                <Select
                  searchable
                  aria-label="Organization"
                  placeholder="Select an organization"
                  value={formData.selectedOrgKey}
                  onChange={(e) => handleInputChange("selectedOrgKey", e.target.value)}
                  options={organizations.map((o) => ({ value: o.id, label: o.name }))}
                  error={errors.selectedOrgKey}
                />
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // Step 2: Upload Data
      const ingestTimezone = getIngestTimezone(existingDataset);
      const finalUpload = uploadResult?.upload || null;
      if (isRetiredForecastDataset) {
        return (
          <div className="rounded-md border border-eai-border bg-eai-surface-sunken p-4" data-testid="retired-forecast-dataset">
            <p className="font-inter text-eai-body-sm font-medium text-eai-ink">Forecast datasets are retired</p>
            <p className="mt-1 text-eai-body-sm text-eai-ink-subtle">
              This dataset can no longer take uploads. Forecasts made outside the platform are uploaded to an external forecast
              model's output (Models → New model → External forecast), where they are indexed and shown like any model's forecasts.
            </p>
          </div>
        );
      }
      return (
        <div className="space-y-6">
          {!uploadMode && (
            <div className="bg-eai-success-surface-soft border border-eai-success-border rounded-md p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-eai-success-text mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-eai-success-text-strong font-medium">Dataset created successfully!</span>
              </div>
              <p className="mt-2 text-eai-body-sm text-eai-success-text">
                <strong>Name:</strong> {formData.name}<br />
                <strong>Slug:</strong> {formData.slug}<br />
                <strong>Type:</strong> {formData.datasetType}
              </p>
            </div>
          )}

          <div>
            <label className="eai-meta-label mb-2 block">
              {uploadMode ? "Select CSV File to Upload" : "Upload CSV Data (Optional)"}
            </label>
            <p className="text-eai-body-sm text-eai-ink-subtle mb-4">
              A CSV with a timestamp column and one or more value columns. The file is checked and loaded by the same pipeline that handles API uploads, and wide files may arrive in pieces.
            </p>

            <div
              className={`rounded-eai-card border-2 border-dashed p-6 text-center ${isUploading ? "border-eai-border opacity-60" : "border-eai-border-hover"}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              data-testid="dataset-upload-dropzone"
            >
              <UploadIcon className="mx-auto h-12 w-12 text-eai-ink-faint" />
              <div className="mt-2">
                <label htmlFor="csv-upload" className={isUploading ? "cursor-default" : "cursor-pointer"}>
                  <span className="text-eai-cta-primary hover:text-[eai-cta-primary-hover] font-medium">
                    Click to upload
                  </span>
                  <span className="text-eai-ink-subtle"> or drag and drop</span>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv,.gz,text/csv,application/gzip"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="sr-only"
                  />
                </label>
              </div>
              <p className="text-eai-body-xs text-eai-ink-subtle mt-1">.csv or .csv.gz, up to 100 MB</p>
            </div>

            {csvFile && !finalUpload && (
              <div className="mt-4 p-3 bg-eai-surface-sunken rounded-md">
                <p className="font-inter text-eai-body-sm font-medium text-eai-ink-muted">
                  Selected: {csvFile.name} <span className="font-normal text-eai-ink-subtle">({formatBytes(csvFile.size)})</span>
                </p>
              </div>
            )}

            {finalUpload && (
              <div
                className={`mt-4 rounded-md border p-3 ${
                  finalUpload.status === "LOADED"
                    ? "border-eai-success-border bg-eai-success-surface-soft"
                    : uploadResult.timedOut
                      ? "border-eai-border bg-eai-surface-sunken"
                      : "border-eai-danger-border bg-eai-danger-surface"
                }`}
                data-testid="dataset-upload-result"
              >
                <p
                  className={`text-eai-body-sm font-medium ${
                    finalUpload.status === "LOADED"
                      ? "text-eai-success-text-strong"
                      : uploadResult.timedOut
                        ? "text-eai-ink-muted"
                        : "text-eai-danger-text"
                  }`}
                >
                  {uploadResult.timedOut
                    ? "Still processing"
                    : finalUpload.status === "LOADED"
                      ? "Upload complete"
                      : finalUpload.status === "REJECTED"
                        ? "File rejected"
                        : finalUpload.status === "FAILED"
                          ? "Processing failed"
                          : "Link expired"}
                  {" — "}
                  {finalUpload.fileName || csvFile?.name}
                </p>
                {uploadResult.timedOut ? (
                  <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                    The file was received and is still being loaded. You can close this window; the outcome will
                    show under Recent uploads.
                    {uploadResult.readError ? ` (Its status could not be read just now: ${uploadResult.readError})` : ""}
                  </p>
                ) : finalUpload.status === "REJECTED" && finalUpload.validationErrors?.length ? (
                  <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-danger-text">
                    {finalUpload.validationErrors.map((problem, i) => (
                      <li key={i}>{problem}</li>
                    ))}
                  </ul>
                ) : (
                  <p className={`mt-1 text-eai-body-xs ${finalUpload.status === "LOADED" ? "text-eai-success-text" : "text-eai-danger-text"}`}>
                    {describeDatasetUpload(finalUpload)}
                  </p>
                )}
                {finalUpload.status === "LOADED" && finalUpload.warnings?.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-warning-text">
                    {finalUpload.warnings.map((warning, i) => (
                      <li key={i}>{warning}</li>
                    ))}
                  </ul>
                )}
                {finalUpload.status === "FAILED" && (
                  <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                    This is a platform error, not a problem with the file; it has been logged for the Erode team.
                  </p>
                )}
              </div>
            )}

            {errors.csvFile && (
              <p className="mt-2 text-eai-body-sm text-eai-danger-text">{errors.csvFile}</p>
            )}
            {errors.upload && (
              <p className="mt-2 text-eai-body-sm text-eai-danger-text">{errors.upload}</p>
            )}
          </div>

          {/* Timezone for timestamps without an offset */}
          {csvFile && !finalUpload && (
            <div className="border-t border-eai-border pt-4">
              <label className="eai-meta-label mb-3 block">
                Timezone of timestamps without an offset
              </label>
              <p className="text-eai-body-xs text-eai-ink-subtle mb-3">
                Timestamps that carry an offset or Z are used as they are.
                {ingestTimezone
                  ? ` Timestamps without one are read in ${ingestTimezone}, this dataset's ingest setting.`
                  : " This dataset has no timezone in its ingest settings, so timestamps without one are read as UTC and the upload carries a warning."}
              </p>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={overrideTimezone}
                  onChange={(e) => setOverrideTimezone(e.target.checked)}
                  className="mr-2"
                />
                <span className="font-inter text-eai-body-sm text-eai-ink-muted">Use a different timezone for this file</span>
              </label>

              {overrideTimezone && (
                <>
                  <div className="flex gap-6 mb-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="named"
                        checked={timezoneType === 'named'}
                        onChange={(e) => setTimezoneType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="font-inter text-eai-body-sm text-eai-ink-muted">Named Timezone</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="offset"
                        checked={timezoneType === 'offset'}
                        onChange={(e) => setTimezoneType(e.target.value)}
                        className="mr-2"
                      />
                      <span className="font-inter text-eai-body-sm text-eai-ink-muted">UTC Offset</span>
                    </label>
                  </div>

                  {timezoneType === 'named' ? (
                    <Select
                      value={sourceTimeZone}
                      onChange={(e) => setSourceTimeZone(e.target.value)}
                      containerClassName="w-full"
                    >
                      {TIME_ZONE_OPTIONS.map((tz) => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-eai-body-sm text-eai-ink-subtle">UTC</span>
                      <Select
                        value={utcOffset >= 0 ? '+' : '-'}
                        onChange={(e) => {
                          const sign = e.target.value === '+' ? 1 : -1;
                          const absOffset = Math.abs(utcOffset);
                          setUtcOffset(sign * absOffset);
                        }}
                        containerClassName="w-16"
                      >
                        <option value="+">+</option>
                        <option value="-">-</option>
                      </Select>
                      <input
                        type="number"
                        min="0"
                        max="14"
                        step="0.5"
                        value={Math.abs(utcOffset)}
                        onChange={(e) => {
                          const absOffset = parseFloat(e.target.value) || 0;
                          const sign = utcOffset >= 0 ? 1 : -1;
                          setUtcOffset(sign * absOffset);
                        }}
                        className="eai-input w-24"
                        placeholder="0"
                      />
                      <span className="text-eai-body-sm text-eai-ink-subtle">hours</span>
                    </div>
                  )}

                  <div className="mt-3 p-2 bg-eai-surface-sunken rounded text-eai-body-xs text-eai-ink-subtle">
                    <strong>Timestamps without an offset will be read in:</strong>{' '}
                    {uploadTimezoneName(timezoneType, sourceTimeZone, utcOffset)} → stored as <strong>UTC</strong>
                  </div>
                </>
              )}
            </div>
          )}

          {uploadMode && existingDataset?.id && (
            <div className="border-t border-eai-border pt-4">
              <DatasetUploadsList datasetId={existingDataset.id} refreshKey={uploadsRefreshKey} />
            </div>
          )}
        </div>
      );
    }
  };

  // Cancel then primary, right-aligned: the wizard's own justify-between footer was the only one in the app, and ModalBase stretches these to full width on a phone.
  const footer =
    currentStep === 1 && !uploadMode ? (
      <>
        <button type="button" onClick={handleClose} className="eai-btn-outline">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting}
          className="eai-btn-cta disabled:pointer-events-none disabled:opacity-40"
        >
          {isSubmitting ? "Creating…" : (<>Next<ChevronRightIcon className="ml-2 h-4 w-4" /></>)}
        </button>
      </>
    ) : uploadResult ? (
      <>
        <button type="button" onClick={handleUploadAnother} className="eai-btn-outline">
          Upload another file
        </button>
        <button type="button" onClick={handleClose} className="eai-btn-cta">
          Done
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          onClick={isUploading ? handleCancelUpload : uploadMode ? handleClose : handleSkipUpload}
          className="eai-btn-outline"
        >
          {isUploading ? (uploadStage === "processing" ? "Stop waiting" : "Cancel upload") : uploadMode ? "Cancel" : "Skip Upload"}
        </button>
        <button
          type="button"
          onClick={handleUploadData}
          disabled={isUploading || !csvFile}
          className="eai-btn-cta disabled:pointer-events-none disabled:opacity-40"
        >
          {isUploading ? (
            <>
              <svg className="-ml-1 mr-3 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {STAGE_LABELS[uploadStage] || "Working…"}
            </>
          ) : (
            <>
              <UploadIcon className="mr-2 h-4 w-4" />
              Upload Data
            </>
          )}
        </button>
      </>
    );

  return (
    <ModalBase
      title={uploadMode ? `Upload Data — ${formData.name}` : (currentStep === 1 ? "Create Dataset" : "Upload Data")}
      icon={<LayersIcon className="h-5 w-5 [&_path]:stroke-current" />}
      onClose={handleClose}
      size="lg"
      footer={footer}
    >
      <div>
          {/* Progress Steps - Hidden in upload mode */}
          {!uploadMode && (
            <div className="flex items-center justify-center mb-8">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-eai-cta-primary' : 'text-eai-ink-faint'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStep >= 1 ? 'border-eai-cta-primary bg-eai-cta-primary text-white' : 'border-eai-border-hover'
              }`}>
                1
              </div>
              <span className="ml-2 text-eai-body-sm font-medium">Dataset Details</span>
            </div>
            <div className="mx-4 w-16 h-0.5 bg-eai-border" />
            <div className={`flex items-center ${currentStep >= 2 ? 'text-eai-cta-primary' : 'text-eai-ink-faint'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStep >= 2 ? 'border-eai-cta-primary bg-eai-cta-primary text-white' : 'border-eai-border-hover'
              }`}>
                2
              </div>
              <span className="ml-2 text-eai-body-sm font-medium">Upload Data</span>
            </div>
          </div>
          )}

          {/* Status Messages */}
          {uploadStatus && (
            <div className="mb-4 p-3 bg-eai-surface-sunken border border-eai-border rounded-md">
              <p className="text-eai-body-sm text-eai-ink-muted">{uploadStatus}</p>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="mb-4 p-4 bg-eai-surface-sunken border border-eai-border rounded-md" data-testid="dataset-upload-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="font-inter text-eai-body-sm font-medium text-eai-ink-muted">{STAGE_LABELS[uploadStage]}</span>
                {uploadStage === "uploading" && <span className="text-eai-body-sm text-eai-ink-subtle">{uploadPercent}%</span>}
              </div>
              <div className="w-full bg-eai-border rounded-full h-2 mb-2 overflow-hidden">
                <div
                  className={`bg-eai-cta-primary h-2 rounded-full transition-all duration-300 ease-in-out ${uploadStage === "processing" ? "animate-pulse" : ""}`}
                  style={{ width: uploadStage === "requesting" ? "5%" : uploadStage === "uploading" ? `${Math.max(5, uploadPercent)}%` : "100%" }}
                ></div>
              </div>
              <p className="text-eai-body-xs text-eai-ink-subtle">{STAGE_DETAILS[uploadStage]}</p>
            </div>
          )}
          {errors.submit && (
            <div className="mb-4 p-3 bg-eai-danger-surface border border-eai-danger-border rounded-md">
              <p className="text-eai-body-sm text-eai-danger-text">{errors.submit}</p>
            </div>
          )}

          {/* Step Content */}
          {renderStepContent()}
      </div>

    </ModalBase>
  );
};

export default HistoricalDatasetModal;
